import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useTheme, Theme } from "@mui/material/styles";
import { useUserPreferences } from "./UserPreferencesContext";
import {
  DEFAULT_VIEW_DENSITY,
  getViewDensityCssVars,
  normalizeViewDensity,
} from "../utils/viewDensity";
import {
  DEFAULT_PRESENTATION_MODE,
  normalizePresentationMode,
} from "../utils/presentationMode";

// export interface ErrorColorsType {
//   backgroundColor: string;
//   borderColor: string;
//   textColor: string;
// }

// export const ErrorColors = (status: string): ErrorColorsType => {
//   const theme = useTheme();

//   switch (status) {
//     case "unavailable":
//       return {
//         backgroundColor: `${theme.palette.warning.light}40`,
//         borderColor: `${theme.palette.warning.light}`,
//         textColor: `${theme.palette.warning.dark}`,
//       };
//     case "offline":
//       return {
//         backgroundColor: `${theme.palette.info.light}40`,
//         borderColor: `${theme.palette.info.light}`,
//         textColor: `${theme.palette.info.dark}`,
//       };
//     case "success":
//       return {
//         backgroundColor: `${theme.palette.success.light}40`,
//         borderColor: `${theme.palette.success.light}`,
//         textColor: `${theme.palette.success.dark}`,
//       };

//     case "error":
//       return {
//         backgroundColor: `${theme.palette.error.light}40`,
//         borderColor: `${theme.palette.error.light}`,
//         textColor: `${theme.palette.error.dark}`,
//       };
//     default:
//       return {
//         backgroundColor: `${theme.palette.success.light}40`,
//         borderColor: `${theme.palette.success.light}`,
//         textColor: `${theme.palette.success.dark}`,
//       };
//   }
// };

interface ColorPaletteContextType {
  theme: Theme;
  colorVars: Record<string, string>;
}

const ColorPaletteContext = createContext<ColorPaletteContextType | null>(null);

export function extractColorVars(theme: Theme, debug = false): Record<string, string> {
  const colorVars: Record<string, string> = {};

  // After all appendColors(...) calls

  const withAlpha = (color: string, alpha: number): string => {
    if (color.startsWith("rgb(")) {
      return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
      // Convert hex to rgba
      const hex = color.replace("#", "");
      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color; // fallback if already rgba or unknown
  };

  const blendOn = (foreground: string, background: string, alpha: number): string => {
    const parseHex = (hex: string) => {
      const cleaned = hex.replace("#", "");
      const bigint = parseInt(cleaned, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return { r, g, b };
    };

    const parseRgb = (rgb: string) => {
      // Handles both rgb(r,g,b) and rgba(r,g,b,a)
      const match = rgb.match(/rgba?\s*\(([^)]+)\)/i);
      if (!match) return null;
      const parts = match[1].split(",").map((p) => p.trim());
      const r = parseInt(parts[0], 10);
      const g = parseInt(parts[1], 10);
      const b = parseInt(parts[2], 10);
      if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
      return { r, g, b };
    };

    const parseColor = (value: string) => {
      if (value.startsWith("#")) {
        return parseHex(value);
      }
      if (value.startsWith("rgb")) {
        return parseRgb(value);
      }
      return null;
    };

    const fg = parseColor(foreground);
    const bg = parseColor(background);
    if (!fg || !bg) {
      // Fallback: if we cannot parse, just return the original foreground
      return foreground;
    }

    const blendChannel = (fgC: number, bgC: number) => {
      const blended = alpha * fgC + (1 - alpha) * bgC;
      return Math.round(blended);
    };

    const rOut = blendChannel(fg.r, bg.r);
    const gOut = blendChannel(fg.g, bg.g);
    const bOut = blendChannel(fg.b, bg.b);

    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(rOut)}${toHex(gOut)}${toHex(bOut)}`;
  };


  const appendColors = (prefix: string, obj: any) => {
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === "string") {
        colorVars[`--${prefix}-${key}`] = value;
      } else if (typeof value === "object" && value !== null) {
        appendColors(`${prefix}-${key}`, value);
      }
    }
  };

  appendColors("primary", theme.palette.primary);
  appendColors("success", theme.palette.success);
  appendColors("error", theme.palette.error);
  appendColors("warning", theme.palette.warning);
  appendColors("info", theme.palette.info);
  appendColors("text", theme.palette.text);
  appendColors("background", theme.palette.background);
  appendColors("divider", { default: theme.palette.divider });
  appendColors("grey", theme.palette.grey);
  // custom palette colors
  appendColors("greensparks", (theme.palette as any).greensparks);

  // Add custom derived color
  // Use the greensparks palette for grid alternate row highlighting
  const greensparksLight = (theme.palette as any).greensparks?.light ?? theme.palette.success.light;
  colorVars["--custom-grid-alt-row"] = withAlpha(greensparksLight, 0.15);
  colorVars["--custom-grid-alt-row-hover"] = withAlpha(greensparksLight, 0.25);

  // Add derived opaque greensparks shades, blended on the surface color.
  // In light mode we blend on white; in dark mode we blend on black, but
  // we keep the same CSS variable names.
  const greensparksPalette = (theme.palette as any).greensparks ?? theme.palette.success;
  const gsLight = greensparksPalette.light ?? theme.palette.success.light;
  const gsMain = greensparksPalette.main ?? theme.palette.success.main;
  const gsDark = greensparksPalette.dark ?? theme.palette.success.dark;

  const isDarkMode = theme.palette.mode === "dark";
  const surfaceColor = isDarkMode ? theme.palette.background.paper : "#ffffff";

  const opacities: [string, number][] = [
    ["20", 0.2],
    ["40", 0.4],
    ["60", 0.6],
    ["80", 0.8],
  ];

  opacities.forEach(([suffix, alpha]) => {
    colorVars[`--greensparks-light${suffix}`] = blendOn(gsLight, surfaceColor, alpha);
    colorVars[`--greensparks-main${suffix}`] = blendOn(gsMain, surfaceColor, alpha);
    colorVars[`--greensparks-dark${suffix}`] = blendOn(gsDark, surfaceColor, alpha);
  });

  colorVars["--panel-accent-surface"] = blendOn(gsLight, surfaceColor, isDarkMode ? 0.16 : 0.34);
  colorVars["--panel-accent-surface-strong"] = blendOn(gsMain, surfaceColor, isDarkMode ? 0.2 : 0.42);
  colorVars["--panel-accent-border"] = blendOn(gsDark, surfaceColor, isDarkMode ? 0.58 : 0.72);
  colorVars["--panel-accent-text"] = theme.palette.text.primary;
  colorVars["--inset-surface"] = theme.palette.background.paper;
  colorVars["--inset-surface-muted"] = theme.palette.background.default;
  colorVars["--inset-border"] = theme.palette.divider;
  colorVars["--inset-text"] = theme.palette.text.primary;
  colorVars["--inset-text-muted"] = theme.palette.text.secondary;
  colorVars["--overlay-header-surface"] = blendOn(gsMain, surfaceColor, isDarkMode ? 0.18 : 0.12);
  colorVars["--overlay-header-border"] = theme.palette.divider;
  colorVars["--overlay-header-text"] = theme.palette.text.primary;
  colorVars["--overlay-header-padding-block"] = "7px";
  colorVars["--overlay-header-padding-inline"] = "10px";
  colorVars["--overlay-body-padding"] = "var(--density-overlay-body-padding)";
  colorVars["--overlay-body-padding-compact"] = "var(--density-overlay-body-padding)";
  colorVars["--overlay-body-gap"] = "var(--density-overlay-body-gap)";
  colorVars["--overlay-body-gap-compact"] = "var(--density-detail-bar-gap)";
  colorVars["--overlay-width-compact"] = "340px";
  colorVars["--overlay-width-medium"] = "420px";
  colorVars["--overlay-width-wide"] = "560px";
  colorVars["--overlay-width-xwide"] = "680px";
  colorVars["--panel-bar-surface"] = isDarkMode ? "hsla(170, 36%, 18%, 0.96)" : "hsla(170, 45%, 92%, 1)";
  colorVars["--panel-bar-surface-strong"] = isDarkMode ? "hsla(170, 38%, 22%, 0.98)" : "hsla(170, 44%, 88%, 1)";
  colorVars["--panel-bar-item-surface"] = isDarkMode ? "hsla(170, 34%, 25%, 0.98)" : "hsla(170, 44%, 96%, 1)";
  colorVars["--panel-bar-border"] = isDarkMode ? "hsla(170, 34%, 38%, 0.9)" : "hsla(170, 42%, 48%, 0.75)";
  colorVars["--toolbar-surface"] = colorVars["--panel-bar-surface"];
  colorVars["--toolbar-border"] = colorVars["--panel-bar-border"];
  colorVars["--toolbar-text"] = theme.palette.text.primary;
  colorVars["--toolbar-radius"] = "0px";
  colorVars["--toolbar-padding-block"] = "var(--density-toolbar-padding-block)";
  colorVars["--toolbar-padding-inline"] = "var(--density-toolbar-padding-inline)";
  colorVars["--toolbar-gap"] = "var(--density-toolbar-gap)";
  colorVars["--toolbar-action-gap"] = "var(--density-inline-gap)";
  colorVars["--toolbar-action-size"] = "var(--density-toolbar-action-size)";
  colorVars["--toolbar-action-radius"] = "8px";
  colorVars["--toolbar-action-surface"] = theme.palette.primary.main;
  colorVars["--toolbar-action-hover"] = theme.palette.primary.dark;
  colorVars["--toolbar-action-text"] = theme.palette.getContrastText(theme.palette.primary.main);
  colorVars["--shell-appbar-bg"] = isDarkMode ? "#0f1714" : "#244c35";
  colorVars["--shell-appbar-text"] = "#ffffff";
  colorVars["--shell-nav-bg"] = isDarkMode ? "#091210" : "#193827";
  colorVars["--shell-nav-border"] = isDarkMode ? "#192622" : "#142e22";
  colorVars["--shell-nav-item-hover"] = isDarkMode ? "#174f2d" : "#2c5f40";
  colorVars["--shell-nav-item-selected"] = isDarkMode ? "#236d36" : "#327247";
  colorVars["--shell-nav-text"] = "#ffffff";
  colorVars["--shell-nav-muted-text"] = isDarkMode ? "rgba(232, 238, 229, 0.62)" : theme.palette.grey[400];
  colorVars["--selection-surface"] = colorVars["--panel-bar-surface"];
  colorVars["--selection-surface-strong"] = colorVars["--panel-bar-surface-strong"];
  colorVars["--selection-hover"] = colorVars["--panel-bar-surface"];
  colorVars["--selection-border"] = colorVars["--panel-bar-border"];
  colorVars["--selection-text"] = theme.palette.text.primary;
  colorVars["--map-control-surface"] = isDarkMode ? "#0f1714" : theme.palette.primary.dark;
  colorVars["--map-control-hover"] = isDarkMode ? "#16231f" : theme.palette.primary.main;
  colorVars["--map-control-border"] = isDarkMode ? "#284038" : theme.palette.primary.dark;
  colorVars["--map-control-icon-filter"] = "invert(1)";
  colorVars["--utility-stack-gap"] = "var(--density-section-gap)";
  colorVars["--utility-inline-gap"] = "var(--density-inline-gap)";
  colorVars["--utility-section-gap"] = "var(--density-section-gap)";
  colorVars["--result-surface-radius"] = "8px";
  colorVars["--result-surface-padding"] = "4px";
  colorVars["--result-surface-margin-top"] = "4px";
  colorVars["--result-surface-bg"] = theme.palette.background.paper;
  colorVars["--result-surface-border"] = theme.palette.divider;
  colorVars["--result-surface-max-height"] = "400px";
  colorVars["--result-row-padding-block"] = "var(--density-result-row-padding-block)";
  colorVars["--result-row-padding-inline"] = "var(--density-result-row-padding-inline)";
  colorVars["--metadata-panel-padding"] = "var(--density-panel-padding)";
  colorVars["--metadata-panel-gap"] = "var(--density-section-gap)";
  colorVars["--metadata-row-gap"] = "var(--density-inline-gap)";
  colorVars["--metadata-panel-radius"] = "10px";
  colorVars["--metadata-label-color"] = theme.palette.text.secondary;
  colorVars["--metadata-value-color"] = theme.palette.text.primary;
  colorVars["--metadata-border"] = theme.palette.divider;
  colorVars["--metadata-surface"] = colorVars["--inset-surface-muted"];
  colorVars["--confirm-warning-surface"] = blendOn(theme.palette.warning.light, surfaceColor, isDarkMode ? 0.24 : 0.3);
  colorVars["--confirm-warning-border"] = theme.palette.warning.main;
  colorVars["--confirm-warning-text"] = theme.palette.warning.dark;
  colorVars["--confirm-danger-action"] = theme.palette.error.main;
  colorVars["--confirm-danger-action-hover"] = theme.palette.error.dark;

  // Add similarly derived shades for core palettes (primary, success, error, warning).
  const addDerivedForPalette = (prefix: string, paletteColor: any) => {
    if (!paletteColor) return;

    const baseLight = paletteColor.light ?? paletteColor.main;
    const baseMain = paletteColor.main;
    const baseDark = paletteColor.dark ?? paletteColor.main;

    opacities.forEach(([suffix, alpha]) => {
      if (baseLight) {
        colorVars[`--${prefix}-light${suffix}`] = blendOn(baseLight, surfaceColor, alpha);
      }
      if (baseMain) {
        colorVars[`--${prefix}-main${suffix}`] = blendOn(baseMain, surfaceColor, alpha);
      }
      if (baseDark) {
        colorVars[`--${prefix}-dark${suffix}`] = blendOn(baseDark, surfaceColor, alpha);
      }
    });
  };

  addDerivedForPalette("primary", theme.palette.primary);
  addDerivedForPalette("success", theme.palette.success);
  addDerivedForPalette("error", theme.palette.error);
  addDerivedForPalette("warning", theme.palette.warning);

  if (debug) {
    console.log("🎨 Extracted CSS color variables:");
    Object.entries(colorVars).forEach(([key, value]) => {
      console.log(`${key}: ${value}`);
    });
  }
  return colorVars;
}

export const ColorPaletteProvider = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  const { preferences } = useUserPreferences();
  const viewDensity = normalizeViewDensity(preferences.viewDensity ?? DEFAULT_VIEW_DENSITY);
  const presentationMode = normalizePresentationMode(preferences.presentationMode ?? DEFAULT_PRESENTATION_MODE);
  const colorVars = useMemo(
    () => ({
      ...extractColorVars(theme, false),
      ...getViewDensityCssVars(viewDensity),
    }),
    [theme, viewDensity]
  ); // set debug = true

  useEffect(() => {
    const root = document.documentElement;
    document.body.setAttribute("data-view-density", viewDensity);
    document.body.setAttribute("data-presentation-mode", presentationMode);
    root.setAttribute("data-view-density", viewDensity);
    root.setAttribute("data-presentation-mode", presentationMode);
    Object.entries(colorVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    return () => {
      Object.keys(colorVars).forEach((key) => {
        root.style.removeProperty(key);
      });
      document.body.removeAttribute("data-view-density");
      document.body.removeAttribute("data-presentation-mode");
      root.removeAttribute("data-view-density");
      root.removeAttribute("data-presentation-mode");
    };
  }, [colorVars, presentationMode, viewDensity]);

  return (
    <ColorPaletteContext.Provider value={{ theme, colorVars }}>
      <div
        style={
          {
            display: "contents",
            ...colorVars,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </ColorPaletteContext.Provider>
  );
};

export const useColorPalette = () => {
  const context = useContext(ColorPaletteContext);
  if (!context) {
    throw new Error("useColorPalette must be used within a ColorPaletteProvider");
  }
  return context;
};
